<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  useAttrs,
  defineProps,
  defineEmits,
  withDefaults,
} from 'vue'
import CUSkeletonLoaderTextField from '@/components/CUSkeletonLoaderTextField.vue'

const props = defineProps<{
  displaySelectAll?: boolean
  skeletonLoader?: boolean
  aggregateSelectionIndex?: number
  itemText?: string
  itemValue?: string
  items?: any[]
  value?: number | string | unknown[] | Record<string, any>
  multiple?: boolean
  required?: boolean
}>()

const emit = defineEmits<{
  (e: 'input', value: any): void
  (e: 'change', value: any): void
}>()

const attrs = useAttrs()
const isAllToggled = ref(true)

// Watch for changes in value to update isAllToggled
watch(
  () => props.value,
  (newVal) => {
    if (!props.multiple) {
      return
    }

    if (newVal?.length === props.items?.length) {
      isAllToggled.value = true
    } else {
      isAllToggled.value = false
    }
  },
  { immediate: true }
)

const listeners = computed(() => {
  return {
    ...attrs,
    input: (event: InputEvent) => {
      emit('input', event)
    },
    change: (event: InputEvent) => {
      emit('change', event)
    },
  }
})

const aggregateSelectionText = (item: any, index: number): string => {
  if (!props.aggregateSelectionIndex || !props.value) {
    return ''
  }

  const appendText =
    index !== props.aggregateSelectionIndex - 1 &&
    props.value.length - 1 > index
      ? ', '
      : ''
  return `${item[props.itemText || 'text']}${appendText}`
}

const toggleAllFilters = (): void => {
  if (!isAllToggled.value) {
    if (props.itemValue) {
      emit(
        'input',
        props.items?.map((item) => item[props.itemValue || 'value'])
      )
    } else {
      emit('input', props.items)
    }
  } else {
    emit('input', [])
  }
}
</script>

<template>
  <CUSkeletonLoaderTextField v-if="skeletonLoader" :label="attrs.label" />
  <div v-else>
    <label v-if="attrs.label" class="font-14">
      {{ attrs.label }}
      <span v-if="required" class="required-asterisk">*</span>
    </label>
    <v-select
      v-bind="attrs"
      :label="null"
      outlined
      :item-text="itemText"
      :item-value="itemValue"
      :items="items"
      :multiple="multiple"
      solo
      flat
      :value="value"
      append-icon="mdi-chevron-down"
      v-on="listeners"
    >
      <template v-if="displaySelectAll" #prepend-item>
        <div
          class="v-list-item primary--text v-list-item--link theme--light v-list-item--highlighted"
          :class="{
            'v-list-item--active': isAllToggled,
          }"
          @click="toggleAllFilters"
        >
          <v-simple-checkbox
            hide-details
            :style="{ 'flex-basis': '13.5%' }"
            color="primary"
            class="margin-y-0 padding-a-0 w-full"
            :value="isAllToggled"
            :ripple="false"
            @click="toggleAllFilters"
          />
          <span class="text-black font-16">All</span>
        </div>
      </template>
      <template
        v-if="aggregateSelectionIndex && !$slots['custom-selection-label']"
        #selection="{ item, index }"
      >
        <span v-if="index < aggregateSelectionIndex" class="margin-l-1">
          {{ aggregateSelectionText(item, index) }}
        </span>
        <span
          v-if="index === aggregateSelectionIndex && value.length"
          class="text-gray-mid-light margin-l-1"
        >
          (+{{ value.length - aggregateSelectionIndex }} others)
        </span>
      </template>
      <template
        v-else-if="!!$slots['custom-selection-label']"
        #selection="{ index }"
      >
        <slot v-if="index === 0" name="custom-selection-label" />
      </template>
      <template v-for="(index, name) in $scopedSlots" #[name]="data">
        <slot :name="name" v-bind="data"></slot>
      </template>
    </v-select>
  </div>
</template>

<style lang="scss" scoped>
.required-asterisk {
  color: $error;
}
</style>
